"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";

interface PopupProps {
  title?: string;
  content?: string;
  buttonText?: string;
  buttonLink?: string;
  image?: string; // Image URL for the popup
  imageAlt?: string; // Alt text for the image
  delay?: number; // Delay in milliseconds before showing popup
  showOnce?: boolean; // Show only once per session
  autoClose?: number; // Auto close after X milliseconds
  onClose?: () => void;
}

const Popup: React.FC<PopupProps> = ({
  title = "Velkommen til CTandKliniken!",
  content = "Book din tid online og få 20% rabat på din første behandling.",
  buttonText = "Book Nu",
  buttonLink = "/bliv-ny-patient-hos-ctandklinik-din-tandlaege-i-glostrup",
  image = "/img/about-1.jpg",
  imageAlt = "Tandlæge behandling",
  delay = 500, // Reduced for testing
  showOnce = true,
  autoClose,
  onClose,
}) => {
  console.log("Popup component rendered with props:", {
    title,
    delay,
    showOnce,
  });
  const [isVisible, setIsVisible] = useState(false);
  const [shouldShow, setShouldShow] = useState(true);

  useEffect(() => {
    console.log("Popup useEffect running", { showOnce, delay, shouldShow });

    // Check if popup should be shown (if showOnce is enabled)
    if (showOnce) {
      const hasShown = localStorage.getItem("popup-shown");
      console.log("localStorage check:", hasShown);
      if (hasShown) {
        console.log("Popup already shown, not showing again");
        setShouldShow(false);
        return;
      }
    }

    console.log("Setting timer for popup with delay:", delay);
    // Show popup after delay
    const timer = setTimeout(() => {
      console.log("Timer fired, shouldShow:", shouldShow);
      if (shouldShow) {
        console.log("Setting popup visible");
        setIsVisible(true);
      }
    }, delay);

    // Auto close if specified
    if (autoClose) {
      const autoCloseTimer = setTimeout(() => {
        setIsVisible(false);
        if (showOnce) {
          localStorage.setItem("popup-shown", "true");
        }
        if (onClose) {
          onClose();
        }
      }, delay + autoClose);

      return () => {
        clearTimeout(timer);
        clearTimeout(autoCloseTimer);
      };
    }

    return () => clearTimeout(timer);
  }, [delay, autoClose, shouldShow, showOnce, onClose]);

  const handleClose = () => {
    setIsVisible(false);

    // Mark as shown in localStorage if showOnce is enabled
    if (showOnce) {
      localStorage.setItem("popup-shown", "true");
    }

    if (onClose) {
      onClose();
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isVisible || !shouldShow) {
    return null;
  }

  return (
    <div
      className="popup-overlay position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
      style={{
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 9999,
      }}
      onClick={handleBackdropClick}
    >
      <div
        className="popup-content bg-white rounded-3 mx-3 overflow-hidden"
        style={{
          maxWidth: "800px",
          width: "100%",
          maxHeight: "90vh",
          animation: "popupSlideIn 0.3s ease-out",
        }}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="btn-close position-absolute top-0 end-0 m-3"
          style={{ zIndex: 10 }}
          aria-label="Luk popup"
        ></button>

        {/* Content Layout */}
        <div className="row g-0 h-100">
          {/* Image Column - Hidden on mobile, visible on tablet+ */}
          <div className="col-md-5 d-none d-md-block position-relative">
            <Image
              src={image}
              alt={imageAlt}
              fill
              className="object-fit-cover"
              style={{ minHeight: "300px" }}
            />
          </div>

          {/* Text Content Column */}
          <div className="col-12 col-md-7 p-4 d-flex flex-column justify-content-center">
            {/* Mobile Image - Only visible on mobile */}
            <div className="d-md-none mb-3 text-center">
              <Image
                src={image}
                alt={imageAlt}
                width={200}
                height={150}
                className="img-fluid rounded object-fit-cover"
              />
            </div>

            {title && (
              <h3 className="popup-title text-primary mb-3 text-center text-md-start">
                {title}
              </h3>
            )}

            {content && (
              <p className="popup-content-text mb-4 text-center text-md-start">
                {content}
              </p>
            )}

            {/* Action buttons */}
            <div className="popup-actions text-center text-md-start">
              <Link
                href={buttonLink}
                className="btn btn-primary btn-lg me-2 mb-2"
                onClick={handleClose}
              >
                {buttonText}
              </Link>

              <button
                onClick={handleClose}
                className="btn btn-outline-secondary mb-2"
              >
                Luk
              </button>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes popupSlideIn {
          from {
            opacity: 0;
            transform: translateY(-50px) scale(0.9);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        .popup-overlay {
          backdrop-filter: blur(2px);
        }

        .popup-content {
          box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.175);
          min-height: 300px;
        }

        @media (max-width: 767px) {
          .popup-content {
            margin: 1rem;
            max-width: calc(100vw - 2rem);
            min-height: auto;
          }

          .popup-content .row {
            min-height: auto;
          }
        }

        @media (min-width: 768px) {
          .popup-content .row {
            min-height: 300px;
          }
        }
      `}</style>
    </div>
  );
};

export default Popup;
